/**
 * The single canonical "what runs next, when, and why" — computed once from the
 * snapshot and rendered identically by the Overview hero, the Schedule hero, and
 * anywhere else that answers the question. Previously each surface derived its own
 * answer with different zone-selection rules, so they could disagree on the next zone.
 */
import type { DashboardState, DaySchedule, PriceInfo, PriceLevel, ScheduleBlock, ScheduleZone, Zone, ZoneType } from "../types";
import { hhmm, savingPct } from "./scheduleCost";

const mmss = (s: number) =>
  `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;

export const stripToday = (label: string) => label.replace(/^Today\s+/, "");
export const cheapLevel = (l?: PriceLevel) => l === "VERY_CHEAP" || l === "CHEAP";

/** The planned block a zone's next run refers to (for level + sunrise proximity). */
export const nextBlockOf = (s: DaySchedule, z: ScheduleZone) =>
  z.nextRunMin == null ? undefined : s.blocks.find((b) => b.zoneId === z.id && b.startMin === z.nextRunMin);

/** The one-line "why" behind a zone's next run — names the actual reasoning, so the
 *  hero subtitle carries the value proposition instead of a generic "cheapest power." */
export function whyPhrase(s: DaySchedule, price: PriceInfo | undefined, z: ScheduleZone): string {
  const b = nextBlockOf(s, z);
  const priceOk = price?.ok !== false;
  const when = z.nextRunLabel ? stripToday(z.nextRunLabel) : null;
  if (z.type === "drip") {
    if (priceOk && cheapLevel(b?.level)) return `Held back to the cheapest power of the day${when ? ` — runs ${when}` : ""}.`;
    return "Flexible window — placed where power is cheapest.";
  }
  // spray: anchored to the cool pre-dawn window
  if (s.sunriseMin != null) {
    const cheap = priceOk && cheapLevel(b?.level);
    return `Cool pre-dawn window, before evaporation peaks at sunrise ${hhmm(s.sunriseMin)}${cheap ? " — and the cheapest power in it" : ""}.`;
  }
  return priceOk && cheapLevel(b?.level) ? "Cheapest power inside the dawn window." : "Cool morning, low evaporation.";
}

/** The causal headline for one planned block — what made the planner pick this slot. */
export function blockReason(s: DaySchedule, b: ScheduleBlock, type: ZoneType): string {
  if (b.kind === "watering") return "Running now — refilling to field capacity.";
  if (b.kind === "done") return "Done — completed this cycle.";
  const day = Math.floor(b.startMin / 1440);
  const priced = s.priceDays[day];
  if (type === "drip") {
    if (priced && cheapLevel(b.level)) return `Cheapest power of the day — anywhere in its window, this slot costs least.`;
    return priced ? "Placed where power is cheapest in its window." : "Earliest feasible slot — prices not published yet.";
  }
  const sr = s.sunriseMin;
  const endLocal = b.startMin - day * 1440 + b.durMin;
  if (sr != null && endLocal <= sr + 20) return `Finishes near sunrise ${hhmm(sr)} — cool air, low evaporation, cheap power.`;
  if (priced && cheapLevel(b.level)) return "Cheapest power inside the dawn window.";
  return "Dawn window — cool air, low evaporation.";
}

export type NextRunKind = "watering" | "next" | "paused" | "none";

export interface NextRun {
  kind: NextRunKind;
  zone: Zone | null;
  name: string;
  type?: ZoneType;
  badge?: string; // "Sprinkler" | "Drip"
  whenLabel: string; // "Watering now" | "Tomorrow" | "Today" | "—"
  eta: string; // big numeral: "04:36" | "21:05" | "Paused" | "—"
  etaCaption: string; // "estimated start" | "left of 35 min" | ""
  why: string;
  soilPct?: number;
  madPct?: number;
  savingsPct: number | null;
}

const badgeOf = (t?: ZoneType) => (t === "drip" ? "Drip" : "Sprinkler");

/**
 * Canonical next-run answer. Prefers the schedule projection (so Overview and
 * Schedule agree); falls back to the live zone list when no schedule is present.
 */
export function computeNextRun(state: DashboardState): NextRun {
  const { zones, paused } = state;
  const schedule = state.schedule;
  const price = state.price;
  const savings = schedule ? savingPct(schedule) : null;

  // 1) Something watering right now — same on every surface.
  const watering = zones.find((z) => z.state === "watering" && z.remaining != null);
  if (watering) {
    return {
      kind: "watering",
      zone: watering,
      name: watering.name,
      type: watering.type,
      badge: badgeOf(watering.type),
      whenLabel: "Watering now",
      eta: mmss(watering.remaining ?? 0),
      etaCaption: watering.total ? `left of ${Math.round(watering.total / 60)} min` : "remaining",
      why: "Running now — refilling to field capacity.",
      soilPct: Math.round(watering.moisture),
      madPct: watering.mad,
      savingsPct: savings,
    };
  }

  if (paused) {
    return {
      kind: "paused",
      zone: null,
      name: "Automatic watering paused",
      whenLabel: "—",
      eta: "Paused",
      etaCaption: "rain delay on",
      why: "Rain delay is on — automatic runs resume when you clear it. Manual runs still work.",
      savingsPct: savings,
    };
  }

  // 2) Next projected run from the schedule (authoritative ordering).
  if (schedule) {
    const upcoming = schedule.zones
      .filter((z) => z.nextRunMin != null && z.nextRunMin >= Math.floor(schedule.nowMin))
      .sort((a, b) => (a.nextRunMin as number) - (b.nextRunMin as number));
    const sz = upcoming[0];
    if (sz) {
      const live = zones.find((z) => z.id === sz.id) ?? null;
      const label = sz.nextRunLabel || "—";
      const time = stripToday(label);
      const dayPart = label.replace(time, "").trim() || "Today";
      return {
        kind: "next",
        zone: live,
        name: sz.name,
        type: sz.type,
        badge: badgeOf(sz.type),
        whenLabel: dayPart,
        eta: time,
        etaCaption: "estimated start",
        why: whyPhrase(schedule, price, sz),
        soilPct: live ? Math.round(live.moisture) : undefined,
        madPct: live?.mad,
        savingsPct: savings,
      };
    }
  }

  // 3) Fallback: live zone list (first below refill, else first idle).
  const fallback =
    zones.find((z) => z.enabled && z.state !== "off" && z.moisture <= z.mad) ??
    zones.find((z) => z.enabled && z.state === "idle") ??
    null;
  if (fallback) {
    return {
      kind: "next",
      zone: fallback,
      name: fallback.name,
      type: fallback.type,
      badge: badgeOf(fallback.type),
      whenLabel: "Next",
      eta: fallback.nextIn || "—",
      etaCaption: "estimated start",
      why: fallback.nextReason || "Scheduled by soil moisture and weather.",
      soilPct: Math.round(fallback.moisture),
      madPct: fallback.mad,
      savingsPct: savings,
    };
  }

  return {
    kind: "none",
    zone: null,
    name: "All zones above refill",
    whenLabel: "—",
    eta: "—",
    etaCaption: "nothing scheduled",
    why: "Every zone is above its refill point — nothing needs water yet.",
    savingsPct: savings,
  };
}
